import React, { useState, useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import useTitanApp from "../../hooks/useTitanApp";
import { useNavigate } from "react-router";
import { setPatientInfoTab } from "../../dispatchers/appDispatchers";
import {
  setConsultationId,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setFreeTypeMode,
  setIsConsultationEnabled,
  setIsFinalSummary,
  setPatientNotes,
  setPatientTranscription,
  setPreConsultationTemplate,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setSignatureDetails,
  setTranscript,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  updateConsultationTemplate,
  setisHandleStartConsultation,
} from "../../dispatchers/patientDispatchers";
import dayjs from "dayjs";
import Skeleton from "@mui/material/Skeleton";
import {
  sendAIQAService,
  setSelectedTemplate,
} from "../../dispatchers/templateDispatchers";
import { ConsultationButtonStatus } from "../../enum/ConsultationButtonStatus";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

function groupBy(array, keyGetter) {
  const map = new Map();
  array.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return Object.fromEntries(map);
}

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.toLocaleDateString("en-US", { weekday: "long" });
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const dayNum = date.getDate();
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${day}, ${month} ${dayNum} at ${time}`;
};

function ActiveConsultations() {
  const { appState, patientState } = useTitanApp();
  const {
    patientConsultations,
    showPatientConsultationsLoader,
    consultationPaused,
    transcript,
  } = patientState || {};
  const { currentPharmacyId, darkMode, companyId } = appState || {};
  const [selectedRange, setSelectedRange] = useState(null);
  const navigate = useNavigate();

  const handleArrowClick = async (patientId, consultationData) => {
    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes === null ? false : true);
    setPreConsultationTemplate(consultationData?.preConsultationTemplate);
    setisHandleStartConsultation(false);
    setPatientNotes(
      consultationData?.notes === null ? "" : consultationData?.notes,
    );

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }
    setConsultationPaused(
      consultationData?.consultationStatus === 3 &&
        !!consultationData?.transcription?.trim()
        ? true
        : false,
    );

    setShowPlayPauseBtn(
      consultationData?.consultationStatus === 3 ? true : false,
    );
    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }

    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationData?.consultationId}`,
    );
    setPatientInfoTab(1);
    setShowNHS111ReferralTab(false);
    if (consultationData?.consultationChat) {
      const parsedAiChatMessages = JSON.parse(
        consultationData?.consultationChat,
      );
      setAiChatSuccessMessages();
      parsedAiChatMessages.forEach((message) => {
        setAddPatientAIChatMessage(message);
      });
    }
    const filledCount = countFilledControls(consultationData?.template);

    if (filledCount < 1 && consultationData.transcription.trim() !== "") {
      const allUserControls =
        consultationData?.template?.templateSections?.flatMap(
          (sec) => sec.userControls,
        ) || [];

      const questions = allUserControls.map((ctrl, idx) => ({
        [`q${idx + 1}`]: ctrl.label,
      }));
      const qaResponse = await sendAIQAService({
        q: questions,
        t: consultationData?.transcription,
      });
      const updatedTemplate = fillTemplateWithQA(
        consultationData?.template,
        qaResponse,
      );
      setSelectedTemplate(updatedTemplate);
      await updateConsultationTemplate({
        consultationId: consultationData?.consultationId,
        payload: updatedTemplate?.templateSections,
      });
    } else {
      setSelectedTemplate(consultationData?.template);
    }
  };
  function countFilledControls(template) {
    let count = 0;
    template?.templateSections?.forEach((section) => {
      section.userControls.forEach((ctrl) => {
        if (ctrl.value && ctrl.value.trim() !== "") count++;
      });
    });
    return count;
  }
  function fillTemplateWithQA(template, qaResponse) {
    let answers = {};
    try {
      answers = JSON.parse(qaResponse?.response || "{}");
    } catch (e) {
      console.error("Invalid QA response JSON", e);
    }

    let counter = 1;
    const updatedSections = template?.templateSections?.map((section) => ({
      ...section,
      userControls: section?.userControls?.map((ctrl) => {
        const key = `a${counter}`;
        const newValue =
          answers.hasOwnProperty(key) && answers[key] !== null
            ? answers[key]
            : ctrl.value;
        counter++;
        return { ...ctrl, value: newValue };
      }),
    }));

    return {
      ...template,
      templateSections: updatedSections,
    };
  }
  const dateRanges = [
    { label: "All", value: "all" },
    { label: "Last 30 days", value: 30 },
    { label: "Last 20 days", value: 20 },
    { label: "Last 10 days", value: 10 },
  ];

  const effectiveDate = (c) =>
    c?.startedOn &&
    c?.createdOn &&
    new Date(c.startedOn) > new Date(c.createdOn)
      ? c.startedOn
      : c?.createdOn;

  const filteredConsultations = useMemo(() => {
    if (!patientConsultations) return [];

    let result = [...patientConsultations];
    result = result.filter((c) => {
      const isCancelled =
        c.consultationButtonStatus === ConsultationButtonStatus.Cancel;
      return (
        (c.consultationStatus === 2 || c.consultationStatus === 3) &&
        !isCancelled
      );
    });

    if (selectedRange && selectedRange !== "all") {
      const daysAgo = dayjs().subtract(selectedRange, "day");
      result = result.filter((c) => dayjs(effectiveDate(c)).isAfter(daysAgo));
    }

    return result.sort(
      (a, b) => new Date(effectiveDate(b)) - new Date(effectiveDate(a)),
    );
  }, [patientConsultations, selectedRange]);

  const grouped = groupBy(filteredConsultations, (c) =>
    formatDateLabel(effectiveDate(c)),
  );

  return (
    <div className="db-consultation-wrapper">
      <div className="consultation-header">
        <div>
          <h3>Active Consultations ({filteredConsultations?.length})</h3>
          <p className="mt-1">
            {selectedRange && selectedRange !== "all"
              ? `Showing consultations from the last ${selectedRange} days`
              : "Showing all consultations."}
          </p>
        </div>
        <Dropdown
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.value)}
          options={dateRanges}
          placeholder="Select Range"
          className="dropdown"
          panelClassName="bucket-dropdown-panel"
        />
      </div>
      {showPatientConsultationsLoader ? (
        <div className="consultation-skeleton-wrapper">
          {[...Array(3)].map((_, i) => (
            <Skeleton
              key={i}
              sx={{
                bgcolor: darkMode ? "grey.800" : "",
                width: "100%",
                height: "70px",
                borderRadius: "16px",
                marginBottom: "10px",
              }}
              variant="rounded"
            />
          ))}
        </div>
      ) : (
        <div className="consultation-list">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date} className="consultation-date-group">
              <div className="consultation-date">
                <p>{date}</p>
              </div>
              {items.map((item) => (
                <div className="consultation-card" key={item?._id}>
                  <div className="consultation-info">
                    <div className="consult-title">
                      <p>{item?.templateName}</p>
                    </div>
                    <div className="consult-subtitle">{item?.serviceName}</div>
                  </div>
                  <div
                    className="consult-arrow"
                    onClick={() => handleArrowClick(item?.patientId, item)}
                    style={{ cursor: "pointer" }}
                  >
                    →
                  </div>
                </div>
              ))}
            </div>
          ))}
          {filteredConsultations.length === 0 && (
            <p style={{ padding: "10px" }}>No consultations in this range.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default ActiveConsultations;
